"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, ReactNode, useState, useMemo } from "react";
import { useRouter } from "@repo/next-utils/router";
import {
  searchApi,
  SearchContext,
  SearchResultType,
  SEARCH_FIELD_LIMIT,
  SEARCH_RESULTS_LIMIT,
} from "@repo/sitecore-lib/topshop-sitecore-components";
import { useStore } from "@repo/ui/store";
import { clientRequest } from "@/utils/request-adapters/client-request";

export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [currentPage, setCurrentPage] = useState<number>(1);

  const page = searchParams!.get("page");
  const query = searchParams!.get("q");

  const [searchResults, setSearchResults] = useState<SearchResultType | null>(
    null,
  );

  const {
    countryData: { countryCode, languages, storeCode },
    shop,
  } = useStore();

  const { language } = languages?.[0] || {};

  const fetchSearchResults = useMemo(() => {
    return async (query: string): Promise<SearchResultType | null> => {
      if (query && query.length <= SEARCH_FIELD_LIMIT) {
        const searchParams = {
          q: query,
          lang: language!,
          store: storeCode,
          country: countryCode,
          shop,
          limit: SEARCH_RESULTS_LIMIT,
          offset: currentPage
            ? (Number(currentPage) - 1) * SEARCH_RESULTS_LIMIT
            : 0,
        };

        const data = await searchApi(clientRequest, searchParams);

        return data;
      } else return null;
    };
  }, [language, storeCode, countryCode, currentPage, shop]);

  const handleSubmit = async (searchInput: string) => {
    if (!searchInput || searchInput === (query as string)) return;

    const newParams = new URLSearchParams(Array.from(searchParams!.entries()));
    newParams.set("q", searchInput);
    newParams.delete("page");

    router.push(`/customer-care/search?${newParams.toString()}`);
  };

  useEffect(() => {
    setCurrentPage(page ? +page : 1);
  }, [page]);

  useEffect(() => {
    if (query) {
      const fetchData = async () => {
        const data = await fetchSearchResults(query);

        setSearchResults(data);
      };

      fetchData();
    }
  }, [query, fetchSearchResults]);

  return (
    <SearchContext.Provider
      value={{
        query,
        searchResults: searchResults?.results,
        totalSearchItems: searchResults?.itemCount,
        currentPage,
        handleSubmit,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
